import { DateTime } from 'luxon';
import React, { useMemo } from 'react';

import { InfoCardVariants } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';

import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Link from '@material-ui/core/Link';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import RefreshIcon from '@material-ui/icons/Refresh';
import { Alert } from '@material-ui/lab';

import { ContentSection } from './ContentSection';
import { CreationStatusPanel } from './CreationStatusPanel';
import { InfoTooltip } from './InfoTooltip';
import { InlineField } from './InlineField';
import { SectionItem } from './SectionItem';
import { StatusIndicator } from './StatusIndicator';
import { useApplicationArgocdStatus } from './hooks/useArgocdStatus';
import { useStyles } from './styles';
import {
  ApplicationStatusResponse,
  ResourceDetail,
  SyncHistoryEntry,
} from './types';
import {
  ANNOTATION_APPLICATION_NAME,
  ANNOTATION_PROJECT_HASH,
  ANNOTATION_PROJECT_NAME,
  compactCellStyle,
  formatDuration,
  formatTimestamp,
  inlineValueStyle,
  statusIndicatorType,
  StatusIndicatorType,
} from './utils';

export interface ArgocdApplicationStatusCardProps {
  variant?: InfoCardVariants;
}

interface SyncBadge {
  type: StatusIndicatorType;
  label: string;
  tooltip?: string;
}

function deriveSyncBadge(data: ApplicationStatusResponse): SyncBadge {
  const opFailed =
    data.operationState === 'Failed' || data.operationState === 'Error';

  if (opFailed) {
    const detail =
      data.syncStatus === 'Synced'
        ? `Synced, but last sync operation failed: ${
            data.operationMessage || 'unknown error'
          }`
        : data.operationMessage || 'Sync operation failed';
    return { type: 'failed', label: 'Failed', tooltip: detail };
  }

  if (data.syncStatus === 'OutOfSync') {
    return {
      type: 'outofsync',
      label: 'OutOfSync',
      tooltip: data.syncStatusMessage,
    };
  }

  if (data.syncStatus === 'Synced') {
    return { type: 'synced', label: 'Synced', tooltip: data.syncStatusMessage };
  }

  return {
    type: 'unknown',
    label: data.syncStatus || 'Unknown',
    tooltip: data.syncStatusMessage,
  };
}

function formatResourceList(
  details: Record<string, ResourceDetail[]>,
): React.ReactNode {
  return (
    <>
      {Object.entries(details).map(([status, resources]) => (
        <React.Fragment key={status}>
          <strong>{status}</strong>
          <br />
          {resources.map((r, i) => (
            <React.Fragment key={i}>
              {'  • '}
              {r.kind}/{r.name}
              <br />
            </React.Fragment>
          ))}
          <br />
        </React.Fragment>
      ))}
    </>
  );
}

const RevisionCell = ({ entry }: { entry: SyncHistoryEntry }) => {
  if (!entry.revision) {
    return (
      <Typography variant="body2" color="textSecondary">
        —
      </Typography>
    );
  }
  const short = entry.revision.substring(0, 7);
  if (entry.commitUrl) {
    return (
      <Link
        href={entry.commitUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{ fontFamily: 'monospace', fontSize: 12 }}
      >
        {short}
      </Link>
    );
  }
  return (
    <Typography
      variant="body2"
      style={{ fontFamily: 'monospace', fontSize: 12, opacity: 0.7 }}
    >
      {short}
    </Typography>
  );
};

const SyncHistory = ({ entries }: { entries: SyncHistoryEntry[] }) => (
  <ContentSection label="Sync History" gridSizes={{ xs: 12 }}>
    <Box mt={1}>
      <Table size="small">
        <TableBody>
          {entries.map((entry, i) => (
            <TableRow key={i}>
              <TableCell style={compactCellStyle}>
                <RevisionCell entry={entry} />
              </TableCell>
              <TableCell style={compactCellStyle}>
                <Tooltip
                  title={DateTime.fromISO(entry.deployedAt).toLocaleString(
                    DateTime.DATETIME_FULL,
                  )}
                  arrow
                >
                  <Typography variant="body2">
                    {formatTimestamp(entry.deployedAt)}
                    {entry.durationSeconds !== undefined && (
                      <Typography
                        variant="body2"
                        component="span"
                        color="textSecondary"
                      >
                        {' '}
                        (took {formatDuration(entry.durationSeconds)})
                      </Typography>
                    )}
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell style={{ ...compactCellStyle, width: 30 }}>
                <InfoTooltip
                  message={
                    entry.initiatedBy
                      ? `Initiated by: ${entry.initiatedBy}`
                      : undefined
                  }
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  </ContentSection>
);

const IssuesOrHistoryPanel = ({
  data,
}: {
  data: ApplicationStatusResponse;
}) => {
  if (data.degradedResources && data.degradedResources.length > 0) {
    return (
      <ContentSection label="Resource Issues" gridSizes={{ xs: 12 }}>
        <Box style={{ maxHeight: 150, overflowY: 'auto' }} mt={1}>
          {data.degradedResources.map((r, i) => (
            <Alert key={i} severity="error" style={{ marginBottom: 4 }}>
              <strong>
                {r.kind}/{r.name}
              </strong>
              {r.namespace && (
                <>
                  {' '}
                  in <strong>{r.namespace}</strong>
                </>
              )}
              : {r.message}
            </Alert>
          ))}
        </Box>
      </ContentSection>
    );
  }

  if (data.syncHistory && data.syncHistory.length > 0) {
    return <SyncHistory entries={data.syncHistory} />;
  }

  return (
    <ContentSection label="Resource Issues" gridSizes={{ xs: 12 }}>
      <Typography
        variant="body2"
        color="textSecondary"
        style={{ marginTop: 8 }}
      >
        No issues found.
      </Typography>
    </ContentSection>
  );
};

interface ResourceBreakdownPanelProps {
  resources: NonNullable<ApplicationStatusResponse['resources']>;
}

const ResourceBreakdownPanel = ({ resources }: ResourceBreakdownPanelProps) => {
  const healthCounts = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(resources.health).filter(([s]) => s !== 'N/A'),
      ),
    [resources.health],
  );
  const healthTotal = useMemo(
    () => Object.values(healthCounts).reduce((sum, c) => sum + c, 0),
    [healthCounts],
  );
  const syncCounts = resources.sync;
  const syncTotal = resources.total;

  return (
    <ContentSection label="Resources" gridSizes={{ xs: 12 }}>
      <SectionItem>
        <Box display="flex" alignItems="flex-start" py={0.5}>
          <Typography
            variant="body1"
            color="textSecondary"
            style={{ minWidth: 100, flexShrink: 0 }}
          >
            Health
          </Typography>
          <Box>
            {Object.entries(healthCounts).map(([status, count]) => (
              <Box key={status} display="flex" alignItems="center" py={0.05}>
                <StatusIndicator
                  type={statusIndicatorType(status)}
                  label={`${count}/${healthTotal} ${status}`}
                />
                <InfoTooltip
                  message={
                    <>
                      {formatResourceList({
                        [status]: resources.healthDetails[status] || [],
                      })}
                      <em>Not all resource types have health information.</em>
                    </>
                  }
                />
              </Box>
            ))}
          </Box>
        </Box>
        <Box display="flex" alignItems="flex-start" py={0.5} mt={0.5}>
          <Typography
            variant="body1"
            color="textSecondary"
            style={{ minWidth: 100, flexShrink: 0 }}
          >
            Sync
          </Typography>
          <Box>
            {Object.entries(syncCounts).map(([status, count]) => (
              <Box key={status} display="flex" alignItems="center" py={0.05}>
                <StatusIndicator
                  type={statusIndicatorType(status)}
                  label={`${count}/${syncTotal} ${status}`}
                />
                <InfoTooltip
                  message={formatResourceList({
                    [status]: resources.syncDetails[status] || [],
                  })}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </SectionItem>
    </ContentSection>
  );
};

export const ArgocdApplicationStatusCard = ({
  variant,
}: ArgocdApplicationStatusCardProps) => {
  const { entity } = useEntity();
  const classes = useStyles();

  const projectName =
    entity.metadata.annotations?.[ANNOTATION_PROJECT_NAME] || '';
  const applicationName =
    entity.metadata.annotations?.[ANNOTATION_APPLICATION_NAME] || '';
  const projectHash =
    entity.metadata.annotations?.[ANNOTATION_PROJECT_HASH] || '';
  const appFullName = `${applicationName}-${projectHash}`;

  const missingAnnotations = !projectName || !applicationName || !projectHash;

  const { data, loading, error, refresh } = useApplicationArgocdStatus(
    missingAnnotations ? '' : appFullName,
    missingAnnotations ? '' : projectName,
  );

  const syncBadge = useMemo(
    () => (data ? deriveSyncBadge(data) : null),
    [data],
  );

  const cardClass =
    variant === 'gridItem' ? classes.gridItemCard : classes.fullHeightCard;
  const cardContentClass =
    variant === 'gridItem'
      ? classes.gridItemCardContent
      : classes.fullHeightCardContent;

  return (
    <Card className={cardClass}>
      <CardHeader
        title="ArgoCD Application Status"
        className={classes.cardHeader}
        action={
          <IconButton
            aria-label="Refresh"
            title="Refresh status"
            onClick={refresh}
          >
            <RefreshIcon />
          </IconButton>
        }
      />
      <Divider />
      <CardContent className={cardContentClass}>
        {missingAnnotations && (
          <Alert severity="info">
            Required DaaS annotations are missing from this entity. Ensure{' '}
            <code>{ANNOTATION_PROJECT_NAME}</code>,{' '}
            <code>{ANNOTATION_APPLICATION_NAME}</code>, and{' '}
            <code>{ANNOTATION_PROJECT_HASH}</code> are set.
          </Alert>
        )}

        {!missingAnnotations && loading && !data && (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress size={32} />
          </Box>
        )}

        {!missingAnnotations && error && !data && (
          <Alert severity="error">
            Failed to fetch ArgoCD status: {error.message}
          </Alert>
        )}

        {!missingAnnotations && data && (
          <Box>
            <CreationStatusPanel
              kind="Application"
              creationStatus={data.creationStatus}
              creationStatusMessage={data.creationStatusMessage}
              conditions={data.conditions}
            />

            {data.creationStatus === 'Succeeded' &&
              data.conditions &&
              data.conditions.length > 0 && (
                <Box mb={2}>
                  <ContentSection
                    label="Application Issues"
                    gridSizes={{ xs: 12 }}
                  >
                    {data.conditions.map((c, i) => (
                      <Alert
                        key={i}
                        severity="warning"
                        style={{ marginBottom: 4 }}
                      >
                        <strong>{c.type}:</strong> {c.message}
                      </Alert>
                    ))}
                  </ContentSection>
                </Box>
              )}

            {data.creationStatus === 'Succeeded' && data.syncStatus && (
              <Grid container spacing={3}>
                <Grid item md={6} xs={12}>
                  <ContentSection
                    label="Application Status"
                    gridSizes={{ xs: 12 }}
                  >
                    <SectionItem>
                      <InlineField label="Health">
                        <StatusIndicator
                          type={statusIndicatorType(data.healthStatus)}
                          label={data.healthStatus}
                        />
                        <InfoTooltip message={data.healthStatusMessage} />
                      </InlineField>
                      {syncBadge && (
                        <InlineField label="Sync">
                          <StatusIndicator
                            type={syncBadge.type}
                            label={syncBadge.label}
                          />
                        </InlineField>
                      )}
                      <InlineField label="Last Sync">
                        <Tooltip
                          title={
                            data.lastSync
                              ? DateTime.fromISO(data.lastSync).toLocaleString(
                                  DateTime.DATETIME_FULL,
                                )
                              : ''
                          }
                          arrow
                        >
                          <Typography variant="body1" style={inlineValueStyle}>
                            {formatTimestamp(data.lastSync)}
                            {data.syncDurationSeconds !== undefined && (
                              <Typography
                                variant="body1"
                                component="span"
                                color="textSecondary"
                              >
                                {' '}
                                (took {formatDuration(data.syncDurationSeconds)}
                                )
                              </Typography>
                            )}
                          </Typography>
                        </Tooltip>
                      </InlineField>
                    </SectionItem>
                  </ContentSection>
                </Grid>

                <Grid item md={6} xs={12}>
                  {data.images && data.images.length > 0 && (
                    <ContentSection
                      label="Container Images"
                      gridSizes={{ xs: 12 }}
                    >
                      <Box
                        component="ul"
                        style={{ margin: 0, paddingLeft: 20 }}
                      >
                        {data.images.map(img => (
                          <li key={img}>
                            <Typography
                              variant="body2"
                              style={{ fontFamily: 'monospace', fontSize: 12 }}
                            >
                              {img}
                            </Typography>
                          </li>
                        ))}
                      </Box>
                    </ContentSection>
                  )}
                </Grid>

                {data.resources && (
                  <Grid item md={6} xs={12}>
                    <ResourceBreakdownPanel resources={data.resources} />
                  </Grid>
                )}

                <Grid item md={6} xs={12}>
                  <IssuesOrHistoryPanel data={data} />
                </Grid>
              </Grid>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
